import { useState, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { productQueries, type Product } from "@/api/product";
import { productAnalyticsQueries } from "@/api/product-analytics";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { LoadingButton } from "@/components/LoadingButton";
import { Card } from "@/components/ui/card";
import { Pencil, Plus, ArrowUpDown } from "lucide-react";
import { ProductDialog } from "./ProductDialog";
import { useToast } from "@/hooks/use-toast";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

/**
 * Displays a table of products with filtering, sorting, usage analytics,
 * and integrates the new fields: automation_product, estimated_cost, product_owner.
 */

export function ProductTabContent() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedFamily, setSelectedFamily] = useState<string>("all");
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: "asc" | "desc";
  }>({ key: "product_family", direction: "asc" });

  // Get current date and 30 days ago for analytics
  const endDate = new Date().toISOString().split("T")[0];
  const startDate = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
    .toISOString()
    .split("T")[0];

  // Fetch products + analytics
  const { data: products = [], isLoading: isLoadingProducts } = useQuery(
    productQueries.products(),
  );
  const { data: analytics, isLoading: isLoadingAnalytics } = useQuery(
    productAnalyticsQueries.summary(startDate, endDate),
  );

  // Build list of product families
  const productFamilies = useMemo(() => {
    const families = [...new Set(products.map((p) => p.product_family))];
    return ["all", ...families];
  }, [products]);

  // Filter & sort
  const filteredAndSortedProducts = useMemo(() => {
    let filtered = [...products];

    // Family filter
    if (selectedFamily !== "all") {
      filtered = filtered.filter((p) => p.product_family === selectedFamily);
    }

    // Sorting
    filtered.sort((a, b) => {
      let aValue: any = a[sortConfig.key as keyof Product];
      let bValue: any = b[sortConfig.key as keyof Product];

      // Special cases for analytics data
      if (sortConfig.key === "daily_usage") {
        aValue =
          analytics?.products[a.product_sku]?.daily_usage?.reduce(
            (sum, day) => sum + day.credits,
            0,
          ) || 0;
        bValue =
          analytics?.products[b.product_sku]?.daily_usage?.reduce(
            (sum, day) => sum + day.credits,
            0,
          ) || 0;
      }

      if (sortConfig.key === "total_usage") {
        aValue = analytics?.products[a.product_sku]?.total_credits || 0;
        bValue = analytics?.products[b.product_sku]?.total_credits || 0;
      }

      if (sortConfig.direction === "asc") {
        return aValue > bValue ? 1 : -1;
      }
      return aValue < bValue ? 1 : -1;
    });

    return filtered;
  }, [products, selectedFamily, sortConfig, analytics]);

  // Handlers
  const handleSort = (key: string) => {
    setSortConfig((current) => ({
      key,
      direction:
        current.key === key && current.direction === "asc" ? "desc" : "asc",
    }));
  };

  const handleCreateClick = () => {
    setSelectedProduct(null);
    setIsDialogOpen(true);
  };

  const handleEditClick = (product: Product) => {
    setSelectedProduct(product);
    setIsDialogOpen(true);
  };

  const handleDialogClose = () => {
    setSelectedProduct(null);
    setIsDialogOpen(false);
  };

  const handleSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ["products"] });
    setIsDialogOpen(false);
    setSelectedProduct(null);
    toast({
      title: "Success",
      description: `Product ${selectedProduct ? "updated" : "created"} successfully`,
    });
  };

  return (
    <>
      <Card className="p-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-2xl font-semibold text-primary mb-2">
              Product Manager
            </h2>
            <p className="text-muted-foreground">
              Manage memetica.ai products and pricing
            </p>
          </div>
          <div className="flex gap-4">
            <Select value={selectedFamily} onValueChange={setSelectedFamily}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Filter by family" />
              </SelectTrigger>
              <SelectContent>
                {productFamilies.map((family) => (
                  <SelectItem key={family} value={family}>
                    {family === "all" ? "All Families" : family}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              onClick={handleCreateClick}
              className="gap-2"
              variant="outline"
            >
              <Plus className="h-4 w-4" />
              Add New Product
            </Button>
          </div>
        </div>

        {isLoadingProducts || isLoadingAnalytics ? (
          <div className="flex justify-center py-8">
            <LoadingButton loading variant="ghost">
              Loading products...
            </LoadingButton>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead
                  onClick={() => handleSort("product_family")}
                  className="cursor-pointer hover:bg-accent w-[250px]"
                >
                  Product Family
                  <ArrowUpDown className="ml-2 h-4 w-4 inline" />
                </TableHead>
                <TableHead
                  onClick={() => handleSort("product_sku")}
                  className="cursor-pointer hover:bg-accent w-[350px]"
                >
                  Product Name
                  <ArrowUpDown className="ml-2 h-4 w-4 inline" />
                </TableHead>
                <TableHead
                  onClick={() => handleSort("credit_cost")}
                  className="cursor-pointer hover:bg-accent text-right"
                >
                  Price (Credits)
                  <ArrowUpDown className="ml-2 h-4 w-4 inline" />
                </TableHead>
                <TableHead className="cursor-pointer hover:bg-accent">
                  Est. Cost
                </TableHead>
                <TableHead
                  onClick={() => handleSort("daily_usage")}
                  className="cursor-pointer hover:bg-accent text-right"
                >
                  Daily Usage
                  <ArrowUpDown className="ml-2 h-4 w-4 inline" />
                </TableHead>
                <TableHead
                  onClick={() => handleSort("total_usage")}
                  className="cursor-pointer hover:bg-accent text-right"
                >
                  Total Usage
                  <ArrowUpDown className="ml-2 h-4 w-4 inline" />
                </TableHead>
                <TableHead
                  onClick={() => handleSort("product_lifecycle")}
                  className="cursor-pointer hover:bg-accent"
                >
                  Lifecycle
                  <ArrowUpDown className="ml-2 h-4 w-4 inline" />
                </TableHead>
                <TableHead className="cursor-pointer hover:bg-accent w-[100px]">
                  Status
                </TableHead>
                {/** NEW COLUMNS FOR automation_product, estimated_cost, product_owner */}
                <TableHead className="cursor-pointer hover:bg-accent w-[100px]">
                  Automated
                </TableHead>

                {/* <TableHead className="cursor-pointer hover:bg-accent w-[100px]">
                  Owner</TableHead> */}

                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredAndSortedProducts.map((product) => (
                <TableRow key={product.product_sku}>
                  <TableCell>
                    <Badge className="font-normal bg-secondary/50">
                      {product.product_family}
                    </Badge>
                  </TableCell>
                  <TableCell className="font-medium">{product.name}</TableCell>
                  <TableCell className="text-right">
                    {product.credit_cost.toFixed(2)}
                  </TableCell>
                  <TableCell>
                    {product.estimated_cost !== undefined
                      ? product.estimated_cost.toFixed(2)
                      : "0.00"}
                  </TableCell>
                  <TableCell className="text-right">
                    {analytics?.products[product.product_sku]?.daily_usage
                      ?.reduce((sum, day) => sum + day.credits, 0)
                      .toFixed(2) || "0.00"}
                  </TableCell>
                  <TableCell className="text-right">
                    {(
                      analytics?.products[product.product_sku]?.total_credits ||
                      0
                    ).toFixed(2)}
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant="secondary"
                      className={`
                        ${product.product_lifecycle === "internal" ? "bg-gray-500/20 text-gray-500" : ""}
                        ${product.product_lifecycle === "private_preview" ? "bg-purple-500/20 text-purple-500" : ""}
                        ${product.product_lifecycle === "public_preview" ? "bg-blue-500/20 text-blue-500" : ""}
                        ${product.product_lifecycle === "ga" ? "bg-green-500/20 text-green-500" : ""}
                      `}
                    >
                      {product.product_lifecycle === "ga"
                        ? "GA"
                        : product.product_lifecycle
                            .split("_")
                            .map(
                              (word) =>
                                word.charAt(0).toUpperCase() + word.slice(1),
                            )
                            .join(" ")}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant={product.active ? "default" : "destructive"}
                      className={
                        product.active ? "bg-green-500/20 text-green-500" : ""
                      }
                    >
                      {product.active ? "Active" : "Inactive"}
                    </Badge>
                  </TableCell>

                  {/** NEW FIELDS */}
                  <TableCell>
                    {product.automation_product ? (
                      <Badge className="bg-blue-500/20 text-blue-600">
                        Yes
                      </Badge>
                    ) : (
                      <Badge
                        variant="secondary"
                        className="text-muted-foreground"
                      >
                        No
                      </Badge>
                    )}
                  </TableCell>

                  {/* <TableCell>
                    {product.product_owner ?? "—"}
                  </TableCell> */}

                  <TableCell className="text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEditClick(product)}
                      className="gap-2 hover:bg-primary/25 hover:text-primary"
                    >
                      <Pencil className="h-4 w-4" />
                      Edit
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      {isDialogOpen && (
        <ProductDialog
          product={selectedProduct}
          open={isDialogOpen}
          onClose={handleDialogClose}
          onSuccess={handleSuccess}
        />
      )}
    </>
  );
}
